import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StickyHeader from '../../components/ui/StickyHeader';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import CalendarHeader from './components/CalendarHeader';
import TodaysBirthdays from './components/TodaysBirthdays';
import CalendarGrid from './components/CalendarGrid';
import BirthdayListView from './components/BirthdayListView';
import QuickMessageModal from './components/QuickMessageModal';
import BirthdayFilters from './components/BirthdayFilters';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';


const BirthdayCalendar = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    timeframe: 'all',
    group: 'all',
    status: 'all'
  });

  // Mock data for contacts with birthdays
  const mockContacts = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@email.com",
      phone: "+1-555-0123",
      dateOfBirth: "1990-08-15",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      notes: "Marketing manager at TechCorp",
      group: "work",
      messageStatus: "sent"
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "michael.chen@email.com",
      phone: "+1-555-0124",
      dateOfBirth: "1985-08-12",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      notes: "College friend, loves photography",
      group: "personal",
      messageStatus: "pending"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.rodriguez@email.com",
      phone: "+1-555-0125",
      dateOfBirth: "1992-08-20",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      notes: "Yoga instructor and wellness coach",
      group: "personal",
      messageStatus: "scheduled"
    },
    {
      id: 4,
      name: "David Thompson",
      email: "david.thompson@email.com",
      phone: "+1-555-0126",
      dateOfBirth: "1988-08-25",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      notes: "Software engineer at StartupXYZ",
      group: "work",
      messageStatus: "failed"
    },
    {
      id: 5,
      name: "Lisa Wang",
      email: "lisa.wang@email.com",
      phone: "+1-555-0127",
      dateOfBirth: "1995-09-10",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
      notes: "Graphic designer and artist",
      group: "personal",
      messageStatus: "pending"
    },
    {
      id: 6,
      name: "James Wilson",
      email: "james.wilson@email.com",
      phone: "+1-555-0128",
      dateOfBirth: "1987-09-15",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      notes: "Business consultant",
      group: "work",
      messageStatus: "sent"
    }
  ];

  // Mock message templates
  const mockMessageTemplates = [
    {
      id: 1,
      name: "Birthday Celebration",
      content: `Happy Birthday {Name}! 🎉\n\nWishing you a fantastic {Age}th birthday filled with joy, laughter, and all your favorite things. Hope your special day is as amazing as you are!\n\nBest wishes,\nYour friend`
    },
    {
      id: 2,
      name: "Professional Birthday",
      content: `Dear {Name},\n\nWarm birthday wishes to you! May this new year of life bring you continued success and happiness.\n\nHope you have a wonderful celebration!\n\nBest regards`
    },
    {
      id: 3,
      name: "Family Birthday",
      content: `Happy {Age}th Birthday {Name}! 🎂\n\nAnother year older, another year more awesome! Can't wait to celebrate with you. Hope your day is filled with love, cake, and all your favorite people.\n\nLove always! ❤️`
    }
  ];

  // Mock contact groups
  const mockContactGroups = [
    { id: 'work', name: 'Work Colleagues' },
    { id: 'personal', name: 'Personal Friends' },
    { id: 'family', name: 'Family Members' },
    { id: 'clients', name: 'Business Clients' }
  ];

  // Get today's birthdays
  const getTodaysBirthdays = () => {
    const today = new Date();
    return mockContacts?.filter(contact => {
      const birth = new Date(contact.dateOfBirth);
      return birth?.getDate() === today?.getDate() && birth?.getMonth() === today?.getMonth();
    });
  };

  // Filter contacts based on current filters
  const getFilteredContacts = () => {
    let filtered = [...mockContacts];

    // Apply search filter
    if (searchQuery?.trim()) {
      filtered = filtered?.filter(contact =>
        contact?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        contact?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        contact?.notes?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply timeframe filter
    if (filters?.timeframe !== 'all') {
      const today = new Date();
      filtered = filtered?.filter(contact => {
        const birth = new Date(contact.dateOfBirth);
        const thisYearBirthday = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
        
        if (thisYearBirthday < today) {
          thisYearBirthday?.setFullYear(today?.getFullYear() + 1);
        }
        
        const daysDiff = Math.ceil((thisYearBirthday?.getTime() - today?.getTime()) / (1000 * 3600 * 24));
        
        switch (filters?.timeframe) {
          case 'thisMonth':
            return birth?.getMonth() === today?.getMonth();
          case 'nextMonth':
            return birth?.getMonth() === (today?.getMonth() + 1) % 12;
          case 'next7Days':
            return daysDiff <= 7;
          case 'next30Days':
            return daysDiff <= 30;
          case 'thisQuarter':
            const currentQuarter = Math.floor(today?.getMonth() / 3);
            const birthQuarter = Math.floor(birth?.getMonth() / 3);
            return birthQuarter === currentQuarter;
          default:
            return true;
        }
      });
    }

    // Apply group filter
    if (filters?.group !== 'all') {
      filtered = filtered?.filter(contact => contact?.group === filters?.group);
    }

    // Apply status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(contact => contact?.messageStatus === filters?.status);
    }

    return filtered;
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleTodayClick = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    console.log('Date clicked:', clickedDate);
  };

  const handleBirthdayClick = (birthday) => {
    if (Array.isArray(birthday)) {
      // Multiple birthdays on same date
      console.log('Multiple birthdays:', birthday);
    } else {
      // Single birthday
      setSelectedContact(birthday);
      setShowMessageModal(true);
    }
  };

  const handleSendWish = (contact) => {
    setSelectedContact(contact);
    setShowMessageModal(true);
  };

  const handleViewContact = (contact) => {
    navigate('/contact-details', { state: { contact } });
  };

  const handleScheduleMessage = (contact) => {
    setSelectedContact(contact);
    setShowMessageModal(true);
  };

  const handleSendMessage = async (messageData) => {
    console.log('Sending message:', messageData);
    
    // Simulate API call
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('Message sent successfully');
        resolve();
      }, 1500);
    });
  };

  const handleExport = () => {
    const filteredContacts = getFilteredContacts();
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Birthday', 'Age', 'Group', 'Status'],
      ...filteredContacts?.map(contact => {
        const birth = new Date(contact.dateOfBirth);
        const age = new Date()?.getFullYear() - birth?.getFullYear();
        return [
          contact?.name,
          contact?.email,
          contact?.phone,
          contact?.dateOfBirth,
          age?.toString(),
          contact?.group,
          contact?.messageStatus
        ];
      })
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `birthday-calendar-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    window.URL?.revokeObjectURL(url);
  };

  const todaysBirthdays = getTodaysBirthdays();
  const filteredContacts = getFilteredContacts();

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <BottomTabNavigation 
        notificationCounts={{ birthdays: todaysBirthdays?.length }}
      />
      {/* Main Content */}
      <div className="lg:ml-60 min-h-screen">
        {/* Sticky Header */}
        <StickyHeader
          searchQuery={searchQuery}
          onSearchQuery={setSearchQuery}
          showAddContact={false}
          onSettingsClick={() => navigate('/settings')}
        />

        {/* Breadcrumbs */}
        <NavigationBreadcrumbs />

        {/* Page Content */}
        <main className="pb-20 lg:pb-8">
          {/* Calendar Header */}
          <CalendarHeader
            currentDate={currentDate}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onTodayClick={handleTodayClick}
          />

          <div className="p-4 lg:p-6 space-y-6">
            {/* Today's Birthdays */}
            {todaysBirthdays?.length > 0 && (
              <TodaysBirthdays
                todaysBirthdays={todaysBirthdays}
                onSendWish={handleSendWish}
                onViewContact={handleViewContact}
              />
            )}

            {/* Filters */}
            <BirthdayFilters
              filters={filters}
              onFiltersChange={setFilters}
              onExport={handleExport}
              contactGroups={mockContactGroups}
            />

            {/* Calendar/List View */}
            {viewMode === 'calendar' ? (
              <CalendarGrid
                currentDate={currentDate}
                birthdays={filteredContacts}
                onDateClick={handleDateClick}
                onBirthdayClick={handleBirthdayClick}
              />
            ) : (
              <BirthdayListView
                birthdays={filteredContacts}
                currentDate={currentDate}
                onSendWish={handleSendWish}
                onViewContact={handleViewContact}
                onScheduleMessage={handleScheduleMessage}
              />
            )}

            {/* Empty State */}
            {filteredContacts?.length === 0 && (
              <div className="bg-surface rounded-lg border border-border shadow-card p-12 text-center">
                <Icon name="Calendar" size={64} className="mx-auto mb-6 text-text-secondary opacity-50" />
                <h3 className="text-xl font-semibold text-text-primary mb-2">
                  No Birthdays Found
                </h3>
                <p className="text-text-secondary mb-6 max-w-md mx-auto">
                  {searchQuery 
                    ? `No birthdays match your search "${searchQuery}". Try adjusting your search terms or filters.`
                    : 'No birthdays match your current filters. Try adjusting your filter settings.'
                  }
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setFilters({ timeframe: 'all', group: 'all', status: 'all' });
                    }}
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Clear Filters
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => navigate('/contact-list')}
                    iconName="UserPlus"
                    iconPosition="left"
                  >
                    Add Contacts
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Quick Message Modal */}
        <QuickMessageModal
          isOpen={showMessageModal}
          onClose={() => {
            setShowMessageModal(false);
            setSelectedContact(null);
          }}
          contact={selectedContact}
          onSendMessage={handleSendMessage}
          messageTemplates={mockMessageTemplates}
        />
      </div>
      {/* Mobile Bottom Navigation */}
      <BottomTabNavigation 
        notificationCounts={{ birthdays: todaysBirthdays?.length }}
      />
    </div>
  );
};

export default BirthdayCalendar;